import React, { useEffect, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { PublicNav } from '../components/PublicNav';
import { MessageGregBubble } from '../components/MessageGregBubble';
import { DemoLeadSection } from '../components/DemoLeadSection';
import { CompareCrossLink } from '../components/CompareCrossLink';
import { BeTheFirstCallout } from '../components/BeTheFirstCallout';
import { findState, TOP_STATES } from '../data/states';
import { MapPin, Calendar, CreditCard, MessageSquare, Users, ShieldCheck, ArrowRight, ChevronRight } from 'lucide-react';

// ----------------------------------------------------------------------
// StateLanding — /dog-training-software/:state
// ----------------------------------------------------------------------
// One template, 50 unique pages. Each page:
//  • Ranks a state-specific H1: "Dog Training Software in {State}"
//  • Ships unique intro text with the state + 3–5 city names
//  • Localized FAQ with state-tailored long-tail phrasing
//  • Injects per-page <title>, meta description, canonical, and
//    JSON-LD (Service + LocalBusiness + FAQPage) via <head> mutations
//  • Uses only Tailwind utilities + Lucide icons — no new deps.
// ----------------------------------------------------------------------

const FEATURES = [
  { icon: Calendar,       title: 'Online booking that never double-books',                desc: 'Fill boarding runs, class rosters, and board-and-train intakes without overlaps.' },
  { icon: CreditCard,     title: 'Built-in POS + monthly memberships',                    desc: 'Sell packages, run recurring VIP billing, and take card payments at the front desk.' },
  { icon: MessageSquare,  title: 'Direct client messaging & live trainer video',         desc: 'Send daily updates, images, and short training clips to owners from any device.' },
  { icon: Users,          title: 'One universal login for owners, trainers & staff',     desc: 'Multi-tier access for Admins, Trainers, Front Desk, and Clients under one system.' },
];

const setMeta = (name, content, attr = 'name') => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const upsertJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const cleanupJsonLd = (id) => {
  const el = document.getElementById(id);
  if (el) el.remove();
};

export default function StateLanding() {
  const { state: slug } = useParams();
  const state = findState(slug);

  const canonical = state ? `https://k9os.app/dog-training-software/${state.slug}` : 'https://k9os.app/dog-training-software';
  const seoTitle = state
    ? `Dog Training Software in ${state.name} — K9os App (${state.abbr}) for Dog Trainers`
    : 'Dog Training Software by State — K9os App';
  const seoDesc = state
    ? `K9os App is the all-in-one dog training software for dog trainers, board-and-train facilities, and kennels across ${state.name} — including ${state.cities.slice(0, 3).join(', ')} and beyond. Online booking, CRM, POS, memberships, digital contracts, messaging, and marketing under one login.`
    : 'K9os App is the all-in-one dog training software available across every U.S. state.';

  const faq = useMemo(() => {
    if (!state) return [];
    const cityList = state.cities.slice(0, 3).join(', ');
    return [
      {
        q: `Is K9os App the best dog training software for dog trainers in ${state.name}?`,
        a: `Yes. K9os App is purpose-built for dog trainers and dog training companies across ${state.name} — from solo trainers in ${state.cities[0]} to multi-trainer board-and-train facilities in ${cityList}. It replaces Square, Revelation, and stitched-together spreadsheets with one login for booking, CRM, POS, memberships, contracts, messaging, and marketing.`,
      },
      {
        q: `Does K9os work for board-and-train facilities in ${state.name}?`,
        a: `Absolutely. K9os supports the full board-and-train workflow — intake forms, signed digital contracts, kennel run grids, daily notes, and live trainer video updates — ready for facilities in ${state.cities[0]}, ${state.cities[1] || state.cities[0]}, and anywhere else in ${state.name}. Be the FIRST board-and-train facility in your city to run on K9os.`,
      },
      {
        q: `Can K9os replace Square and Revelation for my ${state.name} dog training business?`,
        a: `Yes. K9os consolidates Point-of-Sale, scheduling, contracts, memberships, and messaging in one system so ${state.name} dog trainers no longer have to juggle Square, Revelation, spreadsheets, and separate CRMs.`,
      },
      {
        q: `Which cities in ${state.name} does K9os serve?`,
        a: `K9os is a cloud-based platform, so any dog trainer with an internet connection in ${state.name} can run their business on it — including ${state.cities.join(', ')}, and every other city, town, and rural area in the state.`,
      },
      {
        q: `How do dog trainers in ${state.name} get started with K9os?`,
        a: `Head to the Contact page or tap "Book a Demo" on any page to schedule a free walkthrough tailored to your ${state.name} dog training company.`,
      },
    ];
  }, [state]);

  useEffect(() => {
    if (!state) return;
    const prevTitle = document.title;
    document.title = seoTitle;
    setMeta('description', seoDesc);
    setMeta('keywords', `dog training software ${state.name}, dog trainer software ${state.name}, ${state.name} dog training business software, ${state.name} dog trainer CRM, ${state.name} dog trainer POS, board and train software ${state.name}, ${state.cities.map((c) => 'dog trainer ' + c).join(', ')}`);
    setCanonical(canonical);

    // OG / Twitter
    setMeta('og:title', seoTitle, 'property');
    setMeta('og:description', seoDesc, 'property');
    setMeta('og:url', canonical, 'property');
    setMeta('twitter:title', seoTitle);
    setMeta('twitter:description', seoDesc);

    // Structured data — LocalBusiness scoped to state + FAQPage
    upsertJsonLd('k9os-state-jsonld', {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Service',
          '@id': `${canonical}#service`,
          name: `Dog Training Software in ${state.name}`,
          serviceType: 'Dog Training Software',
          provider: { '@id': 'https://k9os.app/#organization' },
          areaServed: {
            '@type': 'State',
            name: state.name,
            containsPlace: state.cities.map((c) => ({ '@type': 'City', name: c })),
          },
          url: canonical,
          description: seoDesc,
        },
        {
          '@type': 'FAQPage',
          '@id': `${canonical}#faq`,
          mainEntity: faq.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://k9os.app/' },
            { '@type': 'ListItem', position: 2, name: 'Dog Training Software by State', item: 'https://k9os.app/dog-training-software' },
            { '@type': 'ListItem', position: 3, name: state.name, item: canonical },
          ],
        },
      ],
    });

    return () => {
      document.title = prevTitle;
      cleanupJsonLd('k9os-state-jsonld');
    };
  }, [state, seoTitle, seoDesc, canonical, faq]);

  if (!state) {
    return <Navigate to='/dog-training-software' replace />;
  }

  return (
    <div className='min-h-screen bg-white dark:bg-slate-950' data-testid='state-landing'>
      <PublicNav />

      {/* Breadcrumb */}
      <div className='border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900'>
        <nav
          aria-label='Breadcrumb'
          className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-xs text-slate-600 dark:text-slate-400 flex items-center gap-1'
          data-testid='state-breadcrumb'
        >
          <Link to='/' className='hover:text-[#1B3A87] dark:hover:text-[#93C5FD]'>Home</Link>
          <ChevronRight className='w-3 h-3 opacity-60' />
          <Link to='/dog-training-software' className='hover:text-[#1B3A87] dark:hover:text-[#93C5FD]'>Dog Training Software by State</Link>
          <ChevronRight className='w-3 h-3 opacity-60' />
          <span className='text-slate-900 dark:text-slate-100 font-medium'>{state.name}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className='relative overflow-hidden bg-gradient-to-b from-white to-slate-50 dark:from-slate-950 dark:to-slate-900'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20'>
          <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1B3A87]/10 text-[#1B3A87] dark:bg-[#93C5FD]/10 dark:text-[#93C5FD] text-xs font-semibold uppercase tracking-[0.18em]'>
            <MapPin className='w-3.5 h-3.5' /> {state.name} · {state.abbr}
          </div>
          <h1
            className='mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight max-w-4xl'
            data-testid='state-h1'
          >
            Dog Training Software in {state.name} <span className='text-[#C8102E]'>&mdash;</span> One App for Dog Trainers, Board-and-Train Facilities &amp; Kennels
          </h1>
          <p className='mt-5 text-slate-600 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-3xl'>
            K9os App is the all-in-one dog training software built for dog trainers and dog training companies across {state.name} &mdash;
            from {state.cities[0]}{state.cities[1] ? ` and ${state.cities[1]}` : ''}{state.cities[2] ? ` to ${state.cities[2]}` : ''}{state.cities[3] ? ` and ${state.cities[3]}` : ''}. Replace
            Square, Revelation, and stitched-together spreadsheets with one login for online booking, CRM, POS, memberships,
            digital contracts, messaging, live video, and marketing.
          </p>
          <div className='mt-7 flex flex-wrap items-center gap-3'>
            <Link
              to='/contact'
              className='inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C8102E] hover:bg-[#A50D26] text-white text-sm font-bold shadow-lg transition-colors'
              data-testid='state-cta-demo'
            >
              Book a Free Demo for Your {state.name} Business <ArrowRight className='w-4 h-4' />
            </Link>
            <Link
              to='/'
              className='inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 hover:border-[#1B3A87] dark:hover:border-[#93C5FD] text-sm font-semibold'
              data-testid='state-cta-tour'
            >
              See the full K9os tour
            </Link>
          </div>

          {/* First-mover promo — encourages the visitor to claim their state
              before another local trainer does. No adoption claims. */}
          <BeTheFirstCallout
            className='mt-8'
            scopeLabel='state'
            areaSuffix={`in ${state.name}`}
            ctaLabel='Claim your area first'
            testid='state-be-the-first'
          />
        </div>
      </section>

      {/* Features */}
      <section className='py-14 md:py-16 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C8102E]/10 text-[#C8102E] dark:bg-[#F87171]/10 dark:text-[#F87171] text-xs font-semibold uppercase tracking-[0.18em]'>
            <ShieldCheck className='w-3.5 h-3.5' /> Built for {state.name} Dog Trainers
          </div>
          <h2 className='mt-4 font-display text-2xl md:text-3xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight max-w-3xl'>
            Everything a {state.name} dog training business runs on &mdash; in one login
          </h2>
          <div className='mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5' data-testid='state-features'>
            {FEATURES.map((f, i) => (
              <div key={i} className='rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all'>
                <div className='w-11 h-11 rounded-xl bg-[#1B3A87]/10 dark:bg-[#93C5FD]/10 text-[#1B3A87] dark:text-[#93C5FD] flex items-center justify-center'>
                  <f.icon className='w-5 h-5' />
                </div>
                <h3 className='mt-4 font-display text-lg font-bold text-[#0B1220] dark:text-slate-50 leading-snug'>{f.title}</h3>
                <p className='mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed'>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cities served */}
      <section className='py-14 md:py-16 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 border-t border-slate-200 dark:border-slate-800'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
          <h2 className='font-display text-2xl md:text-3xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight'>
            Ready for dog trainers in every corner of {state.name}
          </h2>
          <p className='mt-3 text-slate-600 dark:text-slate-300 max-w-3xl leading-relaxed'>
            Because K9os is fully cloud-based, dog trainers, board-and-train facilities, and kennels can run their entire business
            on it from any city, town, or rural area in {state.name} &mdash; and it&rsquo;s open for onboarding right now. Be the FIRST dog trainer or facility in your city
            to launch your own K9os-powered app in these {state.name} metros:
          </p>
          <ul className='mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3' data-testid='state-cities'>
            {state.cities.map((c) => (
              <li
                key={c}
                className='px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-center text-sm font-semibold text-[#1B3A87] dark:text-[#93C5FD] hover:border-[#C8102E] transition-colors'
              >
                {c}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className='py-14 md:py-16 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800'>
        <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1B3A87]/10 text-[#1B3A87] dark:bg-[#93C5FD]/10 dark:text-[#93C5FD] text-xs font-semibold uppercase tracking-[0.18em]'>
            FAQ &middot; {state.name}
          </div>
          <h2 className='mt-4 font-display text-2xl md:text-3xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight'>
            Dog training software questions from {state.name} trainers
          </h2>
          <div className='mt-8 space-y-3' data-testid='state-faq'>
            {faq.map((f, i) => (
              <details
                key={i}
                className='group rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 open:shadow-md'
                {...(i === 0 ? { open: true } : {})}
              >
                <summary className='cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-4'>
                  <h3 className='text-base md:text-lg font-semibold text-[#0B1220] dark:text-slate-50 leading-snug'>{f.q}</h3>
                  <span className='shrink-0 w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 text-[#1B3A87] dark:text-[#93C5FD] flex items-center justify-center transition-transform group-open:rotate-180'>
                    <ChevronRight className='w-4 h-4 rotate-90' />
                  </span>
                </summary>
                <p className='px-5 pb-5 text-sm md:text-[15px] leading-relaxed text-slate-600 dark:text-slate-300'>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + See other states */}
      <section className='py-14 md:py-16 bg-gradient-to-r from-[#1B3A87]/5 via-white to-[#C8102E]/5 dark:from-[#1B3A87]/15 dark:via-slate-900 dark:to-[#C8102E]/15 border-t border-slate-200 dark:border-slate-800'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8 items-center'>
          <div>
            <h2 className='font-display text-2xl md:text-3xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight'>
              Ready to run your {state.name} dog training business on one system?
            </h2>
            <p className='mt-3 text-slate-600 dark:text-slate-300 leading-relaxed'>
              Book a free walkthrough. We&rsquo;ll tailor the demo to your {state.name} facility &mdash; whether you run day training,
              board-and-train, group classes, or a full boarding operation.
            </p>
            <Link
              to='/contact'
              className='mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C8102E] hover:bg-[#A50D26] text-white text-sm font-bold shadow-lg'
              data-testid='state-cta-bottom'
            >
              Book a Free Demo <ArrowRight className='w-4 h-4' />
            </Link>
          </div>
          <div>
            <div className='text-xs uppercase tracking-[0.18em] font-bold text-[#1B3A87] dark:text-[#93C5FD]'>Also serving</div>
            <h3 className='mt-2 font-display text-lg font-bold text-[#0B1220] dark:text-slate-50'>Top dog-training states</h3>
            <ul className='mt-3 flex flex-wrap gap-2' data-testid='state-other-states'>
              {TOP_STATES.filter((s) => s.slug !== state.slug).slice(0, 12).map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/dog-training-software/${s.slug}`}
                    className='px-3 py-1 inline-block rounded-full text-xs font-semibold bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-[#1B3A87] dark:text-[#93C5FD] hover:border-[#C8102E]'
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to='/dog-training-software'
                  className='px-3 py-1 inline-block rounded-full text-xs font-semibold bg-[#1B3A87] text-white hover:bg-[#16306F]'
                >
                  View all 50 states &rarr;
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Cross-link to /compare — boosts SEO PageRank flow and gives the
          visitor a natural "how does K9os compare?" next step. Sits directly
          above the DemoLeadSection so the funnel is: content → compare CTA
          → demo form. */}
      <CompareCrossLink regionName={state.name} />

      {/* Full lead-capture form — one submit per state page.
          Prefills the state name into the location field and tags the
          notes so Greg can trace the source page. */}
      <DemoLeadSection regionName={state.name} regionSlug={state.slug} />

      <MessageGregBubble />
    </div>
  );
}
